import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { type GallerySectionAddition } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type GalleryMetadata } from '~/components/PrivateGallery/PrivateGalleryView';
import { ListSectionHeader } from '~/components/Shared/ListSectionHeader';
import {
  WKSPC_GALLERY_FIRST_SECTION_GAP_DESKTOP,
  WKSPC_GALLERY_FIRST_SECTION_GAP_MOBILE,
} from '~/constants/WorkspaceSpacing';

export const SECTIONS_DISTANCE = 64;

export type UseGallerySectionHeaderParams = {
  isFirstSection: boolean;
  containerHeight?: number;
  containerWidth: number;
  label: string;
  itemCount?: number;
  containerPadding: number;
  onSectionCollapse?: (isCollapsed: boolean) => void;
  renderHeader?: ({
    collapsed,
    onToggleCollapse,
    label,
    itemCount,
    isFirstSection,
  }: {
    collapsed: boolean;
    onToggleCollapse: () => void;
    label: string;
    itemCount?: number;
    isFirstSection: boolean;
  }) => ReactNode;
};

export const useGallerySectionHeader = ({
  isFirstSection,
  containerHeight,
  containerWidth,
  label,
  itemCount,
  containerPadding,
  onSectionCollapse,
  renderHeader,
}: UseGallerySectionHeaderParams): { isHeaderCollapsed: boolean; header: GallerySectionAddition<GalleryMetadata> } => {
  const [isHeaderCollapsed, setHeaderCollapsed] = useState(false);
  const { isAboveMediumScreen } = useBreakpointsContext();

  const headerBox = useMemo(() => {
    const baseHeight =
      containerHeight ??
      (isFirstSection
        ? !isAboveMediumScreen
          ? WKSPC_GALLERY_FIRST_SECTION_GAP_MOBILE
          : WKSPC_GALLERY_FIRST_SECTION_GAP_DESKTOP
        : SECTIONS_DISTANCE + 16);

    const spacing = !isFirstSection ? 24 : 0;

    return {
      aspectRatio: 0,
      left: containerPadding,
      top: 0,
      width: containerWidth - containerPadding,
      height: baseHeight + spacing,
    };
  }, [containerPadding, containerWidth, isFirstSection, isAboveMediumScreen, containerHeight]);

  const onToggleCollapse = useCallback(() => {
    setHeaderCollapsed((collapsed) => {
      onSectionCollapse?.(!collapsed);
      return !collapsed;
    });
  }, [onSectionCollapse]);

  const render = useCallback(() => {
    if (renderHeader) {
      return renderHeader({
        collapsed: isHeaderCollapsed,
        onToggleCollapse,
        label,
        itemCount,
        isFirstSection,
      });
    }

    return (
      <ListSectionHeader
        className={!isFirstSection ? 'pt-6' : ''}
        headerTitle={`${label} (${itemCount?.toLocaleString()})`}
        collapsed={isHeaderCollapsed}
        onClick={onToggleCollapse}
      />
    );
  }, [isHeaderCollapsed, onToggleCollapse, renderHeader, label, itemCount, isFirstSection]);

  return {
    isHeaderCollapsed,
    header: {
      box: headerBox,
      render,
    },
  };
};
